import { useBoundStore } from '@Store/useBoundStore.ts';
import { useCreatePartnerLead } from '@Pages/passwordless/welcome/hooks/useCreatePartnerLead.tsx';
import { useFetchCustomerData } from '@Pages/passwordless/welcome/hooks/useFetchCustomerData.tsx';
import { useNavigationManager } from '@Router/useNavigationManager.ts';
import { useWelcomeAnalytics } from '@Pages/passwordless/welcome/WelcomeAnalytics.tsx';
import { IWelcomeFormFields } from '@Pages/passwordless/welcome/welcomeForm/WelcomeForm.type.ts';
import { OnboardingFormFields } from '@Types/types.ts';
import { ExtendedUseFormReturn, useFormHook, useSetError } from '@Hooks/index.ts';
import { getWelcomeFormScheme } from '@Pages/passwordless/welcome/welcomeForm/WelcomeFormScheme.ts';
import { generateCaptchaToken } from '@Services/init/init-app.service.ts';
import { uploadConsent } from '@Services/api/uploadSnapshot.ts';
import { updateCustomerDataFromServer } from '@Services/init/util.ts';
import { useCreateLead } from '@Pages/passwordless/welcome/hooks/useCreateLead.tsx';
import { isProdEnv } from '@Services/env/environment.service.ts';
import { ICreateLeadResponse } from '@Services/init/partnerWelcome.api.ts';
import { ConsentTypes } from '@Services/api/termsAndConditions';

export const useWelcomePageLogic = () => {
  const { firstName, lastName, email } = useBoundStore((state) => state.customer.customerData);
  const { getCustomerData } = useBoundStore((state) => state);
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();
  const { editMode, isPartnerFlow } = useBoundStore((state) => state.onboardingSlice);
  const { mutateAsync: asyncCreatePartnerLead, isPending: isCreatingPartnerLead } =
    useCreatePartnerLead();
  const { mutateAsync: asyncCreateLead, isPending: isCreatingLead } = useCreateLead();
  const { refetch: asyncGetCustomerData, isLoading: isFetchingCustomerData } =
    useFetchCustomerData();
  const { navigateToNextPage } = useNavigationManager();
  const { reportClickTnC, reportClickPrivacyPolicy, reportActOnSignUpPage } = useWelcomeAnalytics();

  const defaultValues: IWelcomeFormFields = {
    [OnboardingFormFields.FIRST_NAME]: firstName || '',
    [OnboardingFormFields.LAST_NAME]: lastName || '',
    [OnboardingFormFields.EMAIL]: email || '',
    [OnboardingFormFields.PASSWORD]: '',
  };

  const { formMethods } = useFormHook({
    formSchema: getWelcomeFormScheme(isEmbedded),
    formDefaultValues: defaultValues,
    schemaTitle: 'welcome',
  });
  const isValid = formMethods.formState.isValid;
  useSetError({ formMethods: formMethods as ExtendedUseFormReturn<IWelcomeFormFields> });

  const handleSubmit = async () => {
    await generateCaptchaToken();

    reportActOnSignUpPage();

    const formData = formMethods.getValues();

    const payload = {
      firstName: formData[OnboardingFormFields.FIRST_NAME],
      lastName: formData[OnboardingFormFields.LAST_NAME],
      email: formData[OnboardingFormFields.EMAIL],
      password: formData[OnboardingFormFields.PASSWORD],
    };
    let leadData: ICreateLeadResponse | null;
    if (isEmbedded) {
      leadData = await asyncCreatePartnerLead(payload);
    } else {
      leadData = (await asyncCreateLead(payload)) as ICreateLeadResponse;
    }

    if (leadData?.moreData) {
      const customerData = await asyncGetCustomerData();
      updateCustomerDataFromServer(customerData.data, getCustomerData());
    }

    uploadConsent(ConsentTypes.GET_STARTED);
    navigateToNextPage();
  };

  const onPrivacyPolicyClick = () => reportClickPrivacyPolicy();
  const onTncClick = () => reportClickTnC();

  const isLoadingData = isCreatingLead || isCreatingPartnerLead || isFetchingCustomerData;
  return {
    formMethods,
    handleSubmit,
    shouldShowExtendedForm: isPartnerFlow,
    onPrivacyPolicyClick,
    onTncClick,
    isEmbedded,
    isLoading: isLoadingData,
    editMode,
    isButtonDisabled: !isValid || isLoadingData,
    //TODO: This is temporary until we support full direct onboarding and will disable the page's inputs and button. please validate the condition (Aviv)
    elementsDisabled: !isEmbedded && isProdEnv(),
  };
};
